import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { EventDetectTypeSensor } from '@components/dashboard/alert-detail/alert-detail.component';
import { NumeralPipe } from '@pipes/numeral.pipe';
import { format } from 'date-fns';
import {
  SensorAlertDetailModel,
  SensorAlertModel,
} from '../shipment-info-alert-map-dialog.component';

@Component({
  selector: 'app-shipment-sensor-alert-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatSnackBarModule,
    NumeralPipe,
    RouterModule,
  ],
  templateUrl: './shipment-sensor-alert-detail.component.html',
  styleUrl: './shipment-sensor-alert-detail.component.scss',
})
export class ShipmentSensorAlertDetailComponent implements OnChanges {
  @HostBinding('class')
  protected readonly hostClass = 'tw-block';

  @ViewChildren(MatExpansionPanel) panels = new QueryList<MatExpansionPanel>();

  /** アラート一覧 */
  @Input() public alertList: SensorAlertModel | undefined;

  /** センサー値の単位 */
  @Input() public unit: string = '';

  /** 選択されたマーカー情報 */
  @Input() selectedMarker: SensorAlertDetailModel | undefined;

  /** 選択されたアラート詳細 */
  @Output() alertDetailChanged = new EventEmitter<SensorAlertDetailModel>();

  /** 選択マーカーを変更した際に通知 */
  @Output() markerChanged = new EventEmitter<SensorAlertDetailModel>();

  /** 選択されたアラート詳細 */
  public selectedAlertDetail: SensorAlertDetailModel | undefined;

  public eventDetectTypeSensorList: EventDetectTypeSensor[] = [
    'fallDetected',
    'lightExposure',
    'mcdDetected',
    'shockDetected',
  ];

  private readonly sensorNameMap: { [key: string]: string } = {
    temperature: '温度',
    humidity: '湿度',
    pressure: '気圧',
    light: '照度',
    tilt: '傾斜',
    fallDetected: '落下検知',
    mcdDetected: '動作検知',
    lightExposure: '明暗検知',
    shockDetected: '衝撃検知',
  };

  constructor(private snackBar: MatSnackBar) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedMarker'] !== undefined) {
      if (this.selectedMarker == undefined) {
        this.selectedAlertDetail = undefined;
      } else {
        const selectedMarker: SensorAlertDetailModel =
          changes['selectedMarker'].currentValue;
        if (selectedMarker.status) {
          this.filterAlertsByMarker(this.alertList, selectedMarker);
        } else {
          this.selectedAlertDetail = undefined;
        }
      }
    }
  }

  /**
   * センター名を取得する
   *
   * @param sensorType センサータイプ
   * @returns センター名
   */
  public getSensorName(sensorType: string, eventType?: string) {
    let sensorName = this.sensorNameMap[sensorType] ?? '';
    if (sensorType === 'lightExposure') {
      switch (eventType) {
        case 'LightToDark':
          sensorName = `明 → 暗 検出`;
          break;
        case 'DarkToLight':
          sensorName = `暗 → 明 検出`;
          break;
        case 'LightTooBright':
          sensorName = `明暗設定失敗`;
          break;
        case 'OneTimeLightExposureDetected':
          sensorName = `光検出`;
          break;
      }
    }
    return sensorName;
  }

  /**
   * トレース値を返す
   *
   * @param _ インデックス番号
   * @param alert アラート詳細
   * @returns ユニークになる値
   */
  public trackByAlert(_: number, alert: SensorAlertDetailModel) {
    return this.createAlertId(alert);
  }

  /**
   * ユニーク値を返す
   *
   * @param alert アラート詳細
   * @returns ユニークになる値
   */
  public createAlertId(alert: SensorAlertDetailModel) {
    return `${alert.sensor}-${alert.eventAt}`;
  }

  /**
   * 選択したマーカー情報と一致した要素をクラスを付与する
   * @param alertDetail アラート情報
   * @returns 一致の場合True
   */
  public isSelectedAlert(alertDetail: SensorAlertDetailModel) {
    return this.selectedAlertDetail === alertDetail;
  }

  /**
   * アラート一覧をクリック時通知する
   * @param alertDetail アラート情報
   * @param deviceName デバイス名
   * @param deviceId デバイスID
   */
  public selectedAlert(alertDetail: SensorAlertDetailModel) {
    if (
      alertDetail.location?.latitude == null ||
      alertDetail.location?.longitude == null
    ) {
      this.snackBar.open('位置情報がありません。', '閉じる', {
        duration: 5000,
      });
      return;
    }

    // 選択済みアラートを再度クリックしたときは選択済みアラートをundefinedとする。
    // それ以外の場合はクリックしたアラートを選択済みアラートとして保存する。
    if (this.selectedAlertDetail !== alertDetail) {
      this.selectedAlertDetail = alertDetail;
      this.alertDetailChanged.emit({
        ...alertDetail,
      });
    } else {
      this.selectedAlertDetail = undefined;
      this.selectedMarker = undefined;
      this.alertDetailChanged.emit(undefined);
      this.markerChanged.emit(undefined);
    }
  }

  /**
   * マウスオーバー時にポインターと背景色を変更する
   * @param event MouseEvent
   */
  public onMouseEnter(event: MouseEvent) {
    const target = event.target as HTMLElement;
    target.style.cursor = 'pointer';
    target.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
  }

  /**
   * マウスを離れた際元のスタイルに変更する
   * @param event MouseEvent
   */
  public onMouseLeave(event: MouseEvent) {
    const target = event.target as HTMLElement;
    target.style.cursor = '';
    target.style.backgroundColor = '';
  }

  /**
   * マーカー選択時にアラート情報一覧をフィルターする
   * @param alertList アラート情報
   * @param selectedMarker 選択マーカー
   */
  private filterAlertsByMarker(
    alertList: SensorAlertModel | undefined,
    selectedMarker: SensorAlertDetailModel,
  ) {
    alertList?.alertDetails?.forEach((alert) => {
      if (this.equalsAlertDetail(alert, selectedMarker)) {
        this.selectedAlertDetail = alert;
        const alertDetailId = this.createAlertId(alert);

        setTimeout(() => {
          document
            .getElementById(alertDetailId)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    });
  }

  private equalsAlertDetail(
    target: SensorAlertDetailModel,
    source: SensorAlertDetailModel,
  ): boolean {
    let sameEventAt = target.eventAt == source.eventAt;
    if (target.eventAt != null && source.eventAt != null) {
      // フォーマットを揃えて比較する
      sameEventAt =
        format(target.eventAt, 'TT') === format(source.eventAt, 'TT');
    }
    return (
      target.location.latitude === source.location.latitude &&
      target.location.longitude === source.location.longitude &&
      target.sensor === source.sensor &&
      sameEventAt
    );
  }
}
